import React, { useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Clock, Shield, Star, Lock, CreditCard, ArrowDown, Check, X, ShoppingBag } from 'lucide-react';
import { Button } from './ui/Button';
interface ResultsPageProps {
  dogName: string;
  riskLevel: 'ALTO' | 'MODERADO' | 'BAJO';
  onCheckout: () => void;
  selectedCountry?: string;
}
const LOCAL_CURRENCIES: Record<string, {
  symbol: string;
  amount: string;
  amountBump: string;
  name: string;
}> = {
  MX: {
    symbol: '$',
    amount: '299',
    amountBump: '449',
    name: 'MXN'
  },
  CO: {
    symbol: '$',
    amount: '69,900',
    amountBump: '106,900',
    name: 'COP'
  },
  AR: {
    symbol: '$',
    amount: '15,900',
    amountBump: '24,300',
    name: 'ARS'
  },
  CL: {
    symbol: '$',
    amount: '15,900',
    amountBump: '24,300',
    name: 'CLP'
  }
};
const COUNTRY_NAMES: Record<string, string> = {
  MX: 'México',
  CO: 'Colombia',
  AR: 'Argentina',
  CL: 'Chile'
};
const FAKE_BUYERS = [{
  name: 'Laura M.',
  pet: 'Max'
}, {
  name: 'Carlos G.',
  pet: 'Luna'
}, {
  name: 'Ana P.',
  pet: 'Rocky'
}, {
  name: 'Diego R.',
  pet: 'Toby'
}, {
  name: 'Sofía L.',
  pet: 'Coco'
}, {
  name: 'Martín V.',
  pet: 'Lola'
}, {
  name: 'Valentina S.',
  pet: 'Bruno'
}, {
  name: 'Pedro H.',
  pet: 'Milo'
}, {
  name: 'Camila F.',
  pet: 'Canela'
}, {
  name: 'Andrés T.',
  pet: 'Rex'
}];
export function ResultsPage({
  dogName,
  riskLevel,
  onCheckout,
  selectedCountry
}: ResultsPageProps) {
  const [country, setCountry] = useState(selectedCountry || '');
  const [orderBump, setOrderBump] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [socialProof, setSocialProof] = useState<{
    name: string;
    pet: string;
  } | null>(null);
  const [socialProofVisible, setSocialProofVisible] = useState(false);
  // Social proof notifications
  useEffect(() => {
    let buyerIndex = 0;
    const showNotification = () => {
      const buyer = FAKE_BUYERS[buyerIndex % FAKE_BUYERS.length];
      buyerIndex++;
      setSocialProof(buyer);
      setSocialProofVisible(true);
      // Hide after 4 seconds
      setTimeout(() => {
        setSocialProofVisible(false);
      }, 4000);
    };
    // First notification after 8 seconds
    const firstTimeout = setTimeout(showNotification, 8000);
    // Then every 15-25 seconds
    const interval = setInterval(() => {
      showNotification();
    }, 15000 + Math.random() * 10000);
    return () => {
      clearTimeout(firstTimeout);
      clearInterval(interval);
    };
  }, []);
  useEffect(() => {
    // Auto-detect country from timezone if not provided
    if (!country) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz.includes('Mexico')) setCountry('MX');else if (tz.includes('Bogota')) setCountry('CO');else if (tz.includes('Buenos_Aires') || tz.includes('Argentina')) setCountry('AR');else if (tz.includes('Santiago')) setCountry('CL');else setCountry('MX'); // Default to MX
    }
  }, [country]);
  const localCurrency = LOCAL_CURRENCIES[country] || LOCAL_CURRENCIES['MX'];
  const displayPrice = orderBump ? 26 : 17;
  const displayLocalAmount = orderBump ? localCurrency.amountBump : localCurrency.amount;
  const checkoutUrl = orderBump ? 'https://pay.hotmart.com/F104640892L?off=onc7lcga&checkoutMode=10' : 'https://pay.hotmart.com/F104640892L?off=9ore6sbn&checkoutMode=10';
  const handleCheckout = () => {
    if (orderBump) {
      if (window.fbq) window.fbq('track', 'AddToCart', {
        value: 26,
        currency: 'USD'
      });
      window.open('https://pay.hotmart.com/F104640892L?off=onc7lcga&checkoutMode=10', '_blank');
    } else {
      setShowPopup(true);
    }
  };
  return <div className="min-h-screen bg-background pb-20 max-w-md mx-auto shadow-2xl overflow-hidden" data-id="element-147">
      {/* Risk Header — Always ALTO / Orange to create urgency */}
      <div className="bg-white p-6 border-b border-gray-100" data-id="element-148">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-sm mb-4 bg-accent/15 text-accent" data-id="element-149">
          <AlertTriangle className="w-4 h-4" data-id="element-150" />
          ⚠️ Nivel de Riesgo: ALTO
        </div>

        <h1 className="text-3xl font-bold text-text-main mb-2 leading-tight" data-id="element-151">
          {dogName} está envejeciendo más rápido de lo normal
        </h1>
        <p className="text-primary font-medium" data-id="element-152">
          Aquí está lo que encontramos en su perfil:
        </p>
      </div>

      {/* Breakdown Card */}
      <div className="p-6" data-id="element-153">
        <div className="bg-white rounded-xl shadow-sm border-l-4 border-primary p-6 space-y-4" data-id="element-154">
          <h3 className="font-bold text-lg border-b pb-2" data-id="element-155">
            📊 Resumen del Perfil de {dogName}:
          </h3>
          <ul className="space-y-3 text-sm" data-id="element-156">
            <li className="flex justify-between" data-id="element-157">
              <span className="text-text-main/70" data-id="element-158">Factor de riesgo:</span>
              <span className="font-bold text-alert" data-id="element-159">
                Alimentación desequilibrada
              </span>
            </li>
            <li className="flex justify-between" data-id="element-160">
              <span className="text-text-main/70" data-id="element-161">Años ganables:</span>
              <span className="font-bold text-primary" data-id="element-162">
                3-5 años potenciales
              </span>
            </li>
            <li className="flex justify-between" data-id="element-163">
              <span className="text-text-main/70" data-id="element-164">Urgencia:</span>
              <span className="font-bold text-alert" data-id="element-165">Alta</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Education Bridge */}
      <div className="px-6 py-4 space-y-6" data-id="element-166">
        <h2 className="text-2xl font-bold text-text-main" data-id="element-167">
          ¿Por qué está pasando esto?
        </h2>
        <ul className="space-y-4" data-id="element-168">
          {['Los alimentos balanceados industriales usan proteína vegetal de baja calidad que no repara músculo — tu perro pierde masa cada mes', 'Darle solo carne tampoco es la solución: sin las vitaminas, minerales y fibra correctos, los órganos se deterioran silenciosamente', 'Sin omega-3 biodisponible ni antioxidantes naturales, la inflamación articular y el envejecimiento celular avanzan sin síntomas visibles'].map((item, i) => <li key={i} className="flex gap-3 items-start" data-id="element-169">
              <div className="mt-1.5 w-2 h-2 rounded-full bg-alert flex-shrink-0" data-id="element-170" />
              <p className="text-text-main/80 leading-relaxed" data-id="element-171">{item}</p>
            </li>)}
        </ul>
        <p className="text-center italic text-text-main/70 px-4" data-id="element-172">
          "Esto no es culpa tuya. Ni el balanceado industrial ni darle solo
          carne le da a tu perro lo que realmente necesita. La clave está en el
          equilibrio correcto."
        </p>
      </div>

      {/* Solution Transition */}
      <div className="bg-primary/5 px-6 py-8 my-6 text-center" data-id="element-173">
        <div className="bg-green-100 text-green-800 px-4 py-2 rounded-lg inline-block font-bold text-sm mb-4" data-id="element-174">
          ✅ La buena noticia
        </div>
        <p className="text-lg text-text-main font-medium mb-6" data-id="element-175">
          Con el protocolo correcto, perros como {dogName} han ganado 3-5 años
          extra de vida activa.
        </p>
        <div className="flex flex-col items-center gap-2 text-primary font-bold animate-bounce" data-id="element-176">
          <span data-id="element-177">Tu plan está listo abajo</span>
          <ArrowDown className="w-6 h-6" data-id="element-178" />
        </div>
      </div>

      {/* Offer Section */}
      <div id="offer" className="px-4" data-id="element-179">
        <div className="bg-accent text-white text-center py-2 font-bold text-sm rounded-t-xl uppercase tracking-wider" data-id="element-180">
          🎯 Plan Personalizado para {dogName}
        </div>

        <div className="bg-white border-x border-b border-gray-200 rounded-b-xl p-6 shadow-xl" data-id="element-181">
          <h2 className="text-2xl font-bold text-text-main text-center mb-2" data-id="element-182">
            Guía para Alargar 5 Años la Vida de {dogName}
          </h2>
          <p className="text-primary text-center text-sm font-medium mb-6" data-id="element-183">
            Protocolo anti-envejecimiento adaptado para perros adultos
          </p>

          {/* Product Mockup */}
          <div className="mb-8 rounded-xl overflow-hidden shadow-lg border border-gray-100" data-id="element-184">
            <img src="https://cdn.magicpatterns.com/uploads/iQjgGEwXJCnWDaAcATUHEp/heylloyd-image-1772037712321.png" alt="Guía Digital Mockup" className="w-full h-auto object-cover" data-id="element-185" />
          </div>

          {/* Value Stack */}
          <div className="space-y-4 mb-8" data-id="element-186">
            <div className="text-xs font-bold text-text-main/50 uppercase tracking-wider mb-2 text-center" data-id="element-187">
              Lo que incluye:
            </div>
            {[{
            text: 'Protocolo completo 12 semanas de recetas',
            val: '$47'
          }, {
            text: 'Matriz Receta Semanal Anti-Vejez — 7 días x 3 comidas',
            val: '$27'
          }].map((item, i) => <div key={i} className="flex items-start gap-3 p-3 bg-background rounded-lg" data-id="element-188">
                <div className="bg-green-100 p-1 rounded-full mt-0.5" data-id="element-189">
                  <Check className="w-3 h-3 text-green-700" strokeWidth={4} data-id="element-190" />
                </div>
                <div className="flex-1" data-id="element-191">
                  <p className="text-sm font-medium text-text-main" data-id="element-192">
                    {item.text}
                  </p>
                  <p className="text-xs text-text-main/40 line-through" data-id="element-193">
                    Valor: {item.val} USD
                  </p>
                </div>
              </div>)}

            {/* Bonus 1 */}
            <div className="relative p-4 bg-primary/5 rounded-xl border border-primary/15" data-id="element-194">
              <div className="absolute -top-2.5 left-3 bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider" data-id="element-195">
                Bono Exclusivo #1
              </div>
              <div className="mt-2 mb-3 rounded-lg overflow-hidden" data-id="element-196">
                <img src="https://cdn.magicpatterns.com/uploads/hHp1xX4VEkc8sYZQx5oWAB/Analisis_de_comida_para_perros.png" alt="El Diccionario de Etiquetas" className="w-full h-40 object-cover" data-id="element-197" />
              </div>
              <div data-id="element-198">
                <p className="text-sm font-bold text-text-main" data-id="element-199">
                  El Diccionario de Etiquetas
                </p>
                <p className="text-xs font-semibold text-primary mb-1" data-id="element-200">
                  Cómo leer el veneno oculto
                </p>
                <p className="text-xs text-text-main/70 leading-relaxed" data-id="element-201">
                  Aprendé a identificar los 5 ingredientes que las marcas
                  ocultan en las etiquetas y que están envejeciendo a tu perro
                  sin que lo sepas. Guía visual, rápida y sin tecnicismos.
                </p>
                <p className="text-xs text-text-main/40 line-through mt-1" data-id="element-202">
                  Valor: $17 USD
                </p>
              </div>
            </div>

            {/* Bonus 2 */}
            <div className="relative p-4 bg-primary/5 rounded-xl border border-primary/15" data-id="element-203">
              <div className="absolute -top-2.5 left-3 bg-primary text-white text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-wider" data-id="element-204">
                Bono Exclusivo #2
              </div>
              <div className="mt-2 mb-3 rounded-lg overflow-hidden" data-id="element-205">
                <img src="https://cdn.magicpatterns.com/uploads/hNr4j6GHQgqQhg8eh4vrDV/7cc84957-db8a-4db9-ae32-6bd06c8b9eb9.png" alt="Masajes para Aliviar el Dolor" className="w-full h-40 object-cover" data-id="element-206" />
              </div>
              <div data-id="element-207">
                <p className="text-sm font-bold text-text-main" data-id="element-208">
                  Masajes para Aliviar el Dolor
                </p>
                <p className="text-xs font-semibold text-primary mb-1" data-id="element-209">
                  (Digitopuntura Básica para Perros Mayores)
                </p>
                <p className="text-xs text-text-main/70 leading-relaxed" data-id="element-210">
                  Cuando tu perro envejece, el dolor articular se vuelve parte
                  de su día a día — aunque no se queje. Esta guía práctica con
                  mapas de puntos de presión en patas, espalda y cadera te
                  enseña a aliviar ese dolor desde casa, con tus propias manos.
                  Es fundamental para el bienestar de un perro mayor: unos
                  minutos de masaje al día pueden devolverle movilidad, calma y
                  calidad de vida. Resultados visibles desde el primer día.
                </p>
                <p className="text-xs text-text-main/40 line-through mt-1" data-id="element-211">
                  Valor: $12 USD
                </p>
              </div>
            </div>

            {/* Anchor Image — All Deliverables */}
            <div className="rounded-xl overflow-hidden shadow-md border border-primary/15" data-id="element-212">
              <img src="https://cdn.magicpatterns.com/uploads/5uYiGW8RPLMT4TyVaDjuD6/f4d8a914-6cac-4d0a-a135-18df19322843.png" alt="Todos los entregables incluidos" className="w-full h-auto object-cover" data-id="element-213" />
              <div className="bg-primary/5 p-3 text-center" data-id="element-214">
                <p className="text-xs font-bold text-text-main/70 uppercase tracking-wide" data-id="element-215">
                  📦 Todo esto es lo que recibirás hoy
                </p>
              </div>
            </div>
          </div>

          {/* Price Anchor */}
          <div className="text-center mb-8" data-id="element-216">
            <p className="text-text-main/50 line-through text-lg" data-id="element-217">
              Valor total: $103 USD
            </p>
            <div className="flex items-center justify-center gap-3 my-2" data-id="element-218">
              <span className="text-5xl font-extrabold text-accent" data-id="element-219">
                ${displayPrice}
              </span>
              <span className="text-xl font-bold text-text-main" data-id="element-220">USD</span>
            </div>
            {/* Local currency */}
            <div className="flex items-center justify-center gap-2 mt-1 mb-1" data-id="element-221">
              <span className="text-3xl font-extrabold text-text-main" data-id="element-222">
                {localCurrency.symbol}
                {displayLocalAmount}
              </span>
              <span className="text-lg font-bold text-text-main/70" data-id="element-223">
                {localCurrency.name}
              </span>
              <span className="text-xs text-text-main/40 font-medium" data-id="element-224">
                Aprox
              </span>
            </div>
            <p className="text-xs text-text-main/60" data-id="element-225">
              Pago único · Acceso inmediato
            </p>
          </div>

          {/* Guarantee — 7 days */}
          <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex gap-4 items-start mb-6" data-id="element-226">
            <Shield className="w-8 h-8 text-green-600 flex-shrink-0" data-id="element-227" />
            <div data-id="element-228">
              <h4 className="font-bold text-green-800 text-sm" data-id="element-229">
                Garantía Total 7 Días
              </h4>
              <p className="text-xs text-green-700 mt-1" data-id="element-230">
                Si no ves mejoras en la energía de {dogName}, te devolvemos el
                100% de tu dinero. Sin preguntas.
              </p>
            </div>
          </div>

          {/* Order Bump — Protocolo Detox Canino */}
          <div onClick={() => setOrderBump(!orderBump)} className={`border-2 border-dashed rounded-xl overflow-hidden mb-6 transition-colors animate-heartbeat cursor-pointer ${orderBump ? 'border-amber-400 bg-amber-50/30' : 'border-amber-300/60 bg-amber-50/10'}`} data-id="element-231">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-4 py-2 text-center" data-id="element-232">
              <span className="text-white text-xs font-bold uppercase tracking-wider" data-id="element-233">
                Le puede servir a {dogName}
              </span>
            </div>

            <div className="p-4 space-y-3" data-id="element-234">
              {/* Checkbox */}
              <div className="flex items-start gap-3 select-none" data-id="element-235">
                <input type="checkbox" id="orderBump" checked={orderBump} readOnly className="mt-0.5 w-5 h-5 text-amber-500 rounded border-amber-300 focus:ring-amber-400 flex-shrink-0 pointer-events-none" data-id="element-236" />
                <span className="text-sm font-extrabold text-text-main leading-snug" data-id="element-237">
                  SÍ, QUIERO EVITAR EL SUFRIMIENTO DE MI PERRO{' '}
                  <span className="text-amber-600" data-id="element-238">
                    ($9 USD ≈ {localCurrency.symbol}
                    {country === 'MX' ? '159' : country === 'CO' ? '37,000' : country === 'AR' ? '8,400' : '8,400'}{' '}
                    {localCurrency.name} Aprox)
                  </span>
                </span>
              </div>

              {/* Title */}
              <div data-id="element-239">
                <h4 className="text-base font-bold text-text-main" data-id="element-240">
                  🛡️ Protocolo Detox Canino
                </h4>
              </div>

              {/* Body */}
              <p className="text-xs text-text-main/75 leading-relaxed" data-id="element-241">
                ¿Tu perro come pasto o lame el suelo sin parar? No es un hábito:
                es <strong data-id="element-242">dolor por acidez y toxinas acumuladas</strong>. En un
                perro mayor, esta "purga" lo debilita, agota su corazón, lastima
                su esófago… y muchas veces termina en vómitos.
              </p>
              <p className="text-xs text-text-main/75 leading-relaxed" data-id="element-243">
                Incorporá <strong data-id="element-244">5 recetas de detox suave</strong> para cortar
                la acidez de raíz, ayudar a frenar los vómitos y{' '}
                <strong data-id="element-245">devolverle una pancita en paz hoy mismo.</strong>
              </p>

              {/* Visual indicators */}
              <div className="flex items-center justify-center gap-4 pt-1" data-id="element-246">
                <div className="flex items-center gap-1.5 text-[10px] text-red-500 font-bold" data-id="element-247">
                  <span className="bg-red-100 rounded-full w-5 h-5 flex items-center justify-center text-xs" data-id="element-248">
                    ✕
                  </span>
                  Comer pasto
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-red-500 font-bold" data-id="element-249">
                  <span className="bg-red-100 rounded-full w-5 h-5 flex items-center justify-center text-xs" data-id="element-250">
                    ✕
                  </span>
                  Lamer suelos
                </div>
                <div className="flex items-center gap-1.5 text-[10px] text-green-600 font-bold" data-id="element-251">
                  <span className="bg-green-100 rounded-full w-5 h-5 flex items-center justify-center text-xs" data-id="element-252">
                    ✓
                  </span>
                  Pancita en paz
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <Button onClick={handleCheckout} fullWidth size="xl" className="shadow-xl shadow-orange-200 mb-4 animate-pulse-slow" data-id="element-253">
            🐾 Quiero Salvar a {dogName}
          </Button>

          {/* Trust + Payment Logos */}
          <div className="flex justify-center text-xs text-green-600 font-bold mb-4" data-id="element-254">
            <span className="flex items-center gap-1.5" data-id="element-255">
              <Lock className="w-4 h-4" data-id="element-256" /> Pago Seguro
            </span>
          </div>

          {/* Payment method logos */}
          <div className="flex items-center justify-center gap-3 flex-wrap px-2" data-id="element-257">
            <div className="bg-white border border-gray-200 rounded px-3 py-1.5 flex items-center justify-center h-8" data-id="element-258">
              <span className="text-[11px] font-bold text-blue-700 tracking-wider" data-id="element-259">
                VISA
              </span>
            </div>
            <div className="bg-white border border-gray-200 rounded px-3 py-1.5 flex items-center justify-center h-8" data-id="element-260">
              <div className="flex items-center" data-id="element-261">
                <div className="w-5 h-5 rounded-full bg-red-500 opacity-85" data-id="element-262"></div>
                <div className="w-5 h-5 rounded-full bg-yellow-400 opacity-85 -ml-2" data-id="element-263"></div>
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded px-3 py-1.5 flex items-center justify-center h-8" data-id="element-264">
              <span className="text-[10px] font-bold text-blue-500" data-id="element-265">AMEX</span>
            </div>
            <div className="bg-white border border-gray-200 rounded px-3 py-1.5 flex items-center justify-center h-8" data-id="element-266">
              <span className="text-[10px] font-bold text-blue-800" data-id="element-267">
                PayPal
              </span>
            </div>
            <div className="bg-white border border-gray-200 rounded px-2 py-0.5 flex items-center justify-center h-8" data-id="element-268">
              <img src="https://cdn.magicpatterns.com/uploads/35Hq3qT9VQqa25NveE3Mtb/pasted-image.png" alt="Mercado Pago" className="h-7 w-auto object-contain" data-id="element-269" />
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials — Each addresses a specific objection */}
      <div className="px-6 py-12 space-y-6" data-id="element-270">
        {[{
        text: 'Pensé que para mi perro de 13 años ya era demasiado tarde porque es muy grande. Pero a las 3 semanas ya lo veo con más energía, sube las escaleras solo y hasta juega otra vez. Ojalá hubiera empezado antes.',
        author: 'Carmen R., Guadalajara, México',
        avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=200&h=200&auto=format&fit=crop&crop=face',
        selfie: 'https://cdn.magicpatterns.com/uploads/g44QC8N6G5MVZjAeBVNTtf/55a6b42d-d313-4ab2-ad95-006eaea3cab3.png',
        selfieClass: 'object-top'
      }, {
        text: 'Tengo 2 perros, uno de raza y otro mestizo. No me imaginaba que esto sirviera en cualquier tipo de perro por igual. Los dos mejoraron su pelaje y energía en un mes. Fue un alivio enorme.',
        author: 'María José S., Bogotá, Colombia',
        avatar: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=200&h=200&auto=format&fit=crop&crop=face',
        selfie: 'https://cdn.magicpatterns.com/uploads/3ohKmhXLVkDqJ4DqtndWXW/4e7a7a62-9b44-4ff2-8284-17c42ad0d8d0.png',
        selfieClass: 'object-top'
      }, {
        text: 'Me daba miedo cocinarle yo misma, pensé que iba a ser complicado o que necesitaba ingredientes raros. Pero las recetas usan cosas que ya tengo en mi cocina. En 15 minutos está listo y mi Luna come feliz.',
        author: 'Roberto M., Buenos Aires, Argentina',
        avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&h=200&auto=format&fit=crop&crop=face',
        selfie: 'https://cdn.magicpatterns.com/uploads/rnbwBNmuCFtezxqPU8mkWc/85a00881-104b-4d04-ab73-6842b75a83a3.png',
        selfieClass: 'object-top'
      }].map((t, i) => <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-271">
            <div className="p-5" data-id="element-272">
              <div className="flex items-center gap-3 mb-3" data-id="element-273">
                <img src={t.avatar} alt={t.author} className="w-12 h-12 rounded-full object-cover border-2 border-primary/20" data-id="element-274" />
                <div data-id="element-275">
                  <div className="flex gap-0.5 text-accent" data-id="element-276">
                    {[1, 2, 3, 4, 5].map(s => <Star key={s} className="w-3.5 h-3.5 fill-current" data-id="element-277" />)}
                  </div>
                  <p className="text-xs font-bold text-text-main/60 mt-0.5" data-id="element-278">
                    — {t.author}
                  </p>
                </div>
              </div>
              <p className="text-text-main/80 italic text-sm leading-relaxed" data-id="element-279">
                "{t.text}"
              </p>
            </div>
            {/* Selfie with dog */}
            <img src={t.selfie} alt={`${t.author} con su perro`} className={`w-full h-56 object-cover border-t border-gray-100 ${t.selfieClass}`} data-id="element-280" />
          </div>)}
      </div>

      {/* Final CTA after testimonials */}
      <div className="px-6 pb-8" data-id="element-281">
        <Button onClick={handleCheckout} fullWidth size="xl" className="shadow-xl shadow-orange-200 animate-pulse-slow" data-id="element-282">
          🐾 Quiero Salvar a {dogName}
        </Button>
        <p className="text-center text-[10px] text-text-main/40 mt-3 uppercase tracking-wider" data-id="element-283">
          Pago único · Acceso inmediato · Garantía 7 días
        </p>
      </div>

      {/* Social Proof Toast */}
      {socialProof && <div className={`fixed bottom-4 left-4 right-4 z-40 max-w-sm mx-auto transition-all duration-500 ${socialProofVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0 pointer-events-none'}`} data-id="element-284">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 flex items-center gap-3" data-id="element-285">
            <div className="bg-green-100 rounded-full p-2 flex-shrink-0" data-id="element-286">
              <ShoppingBag className="w-4 h-4 text-green-600" data-id="element-287" />
            </div>
            <div className="flex-1 min-w-0" data-id="element-288">
              <p className="text-xs font-bold text-text-main truncate" data-id="element-289">
                {socialProof.name} compró la guía para {socialProof.pet}
              </p>
              <p className="text-[10px] text-text-main/50" data-id="element-290">
                Hace unos minutos · {COUNTRY_NAMES[country] || 'México'}
              </p>
            </div>
            <div className="text-green-500 text-xs font-bold flex-shrink-0" data-id="element-291">
              ✓
            </div>
          </div>
        </div>}

      {/* Popup — Upsell Detox */}
      {showPopup && <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-id="element-292">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPopup(false)} data-id="element-293" />

          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4 animate-in fade-in zoom-in-95" data-id="element-294">
            {/* Close */}
            <button onClick={() => setShowPopup(false)} className="absolute top-3 right-3 text-text-main/40 hover:text-text-main" data-id="element-295">
              <X className="w-5 h-5" data-id="element-296" />
            </button>

            {/* Content */}
            <p className="text-lg font-bold text-text-main text-center" data-id="element-297">
              Espera, te faltó agregar esto:
            </p>
            <p className="text-sm text-text-main/70 text-center" data-id="element-298">
              🛡️ El <strong data-id="element-299">Protocolo Detox Canino</strong> por solo{' '}
              <strong data-id="element-300">$9 USD</strong> — para frenar los vómitos y el consumo de
              pasto.
            </p>

            {/* Buttons */}
            <div className="space-y-3 pt-2" data-id="element-301">
              <button onClick={() => {
            if (window.fbq) window.fbq('track', 'AddToCart', {
              value: 26,
              currency: 'USD'
            });
            window.open('https://pay.hotmart.com/F104640892L?off=onc7lcga&checkoutMode=10', '_blank');
            setShowPopup(false);
          }} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-white font-bold text-sm py-3.5 px-4 rounded-xl shadow-lg hover:shadow-xl transition-shadow" data-id="element-302">
                Quiero la guía y además el Protocolo Detox para mi mascota
              </button>
              <button onClick={() => {
            if (window.fbq) window.fbq('track', 'AddToCart', {
              value: 17,
              currency: 'USD'
            });
            window.open('https://pay.hotmart.com/F104640892L?off=9ore6sbn&checkoutMode=10', '_blank');
            setShowPopup(false);
          }} className="w-full text-text-main/50 text-xs py-2.5 px-4 rounded-lg hover:text-text-main/70 transition-colors underline underline-offset-2" data-id="element-303">
                Solo quiero la guía para alargar la vida de mi mascota
              </button>
            </div>
          </div>
        </div>}
    </div>;
}